import type { ProductInfo } from "@expert/contentful";
import type { JSX } from "react";

import { useProductOfferStore } from "@expert/sales-tools";
import { Button, Container, Flex, Stack, Text } from "@mantine/core";

import { useSessionId } from "@/sdk";

import { EligibilityStatus, SaleProgress } from "../../types";
import { getSalesViewConfig } from "./SaleStatusMap";
import classes from "./SaleView.module.css";

interface SaleViewProps {
    status: EligibilityStatus | SaleProgress;
    planType?: "Home" | "Other";
    onPrimarySaleComplete?: () => void;
    productInfo?: ProductInfo;
}

export function SaleView({
    status,
    planType = "Home",
    onPrimarySaleComplete,
    productInfo,
}: SaleViewProps): JSX.Element {
    const sessionId = useSessionId();
    const hasOtherProducts = useProductOfferStore((state) =>
        state.getOtherProducts(sessionId).some((p) => p.status === EligibilityStatus.Eligible),
    );

    const { statusHeader, statusBody, icon } = getSalesViewConfig(status);

    return (
        <>
            <Flex
                className={status !== SaleProgress.Declined ? classes.statusContainer : classes.declinedContainer}
                p={14}
                data-testid="sale-finished-view"
            >
                <Stack gap={0}>
                    <Text fw={700} fz="sm" className={classes.statusHeader} data-testid="sale-view-status-header">
                        {statusHeader}
                    </Text>
                    <Text className={classes.statusBodyText} fz="xs" pt={4} data-testid="sale-view-status-body">
                        {status === SaleProgress.Success && productInfo?.saleSuccessMessage
                            ? productInfo?.saleSuccessMessage
                            : statusBody(planType, hasOtherProducts)}
                    </Text>
                </Stack>

                <Container className={status !== SaleProgress.Declined ? classes.statusIcon : classes.declinedIcon}>
                    {icon}
                </Container>
            </Flex>
            <Flex className={classes.footerBox}>
                <Button
                    color={"white"}
                    fz="xs"
                    size="sm"
                    variant={"outline"}
                    data-testid="close-sales-view"
                    onClick={onPrimarySaleComplete}
                >
                    Close
                </Button>
            </Flex>
        </>
    );
}
